import { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
} from '@mui/material';
import type { BrokerMetadata } from './types';
import type { WizardStep1Data } from './BrokerRegistrationWizardStep1';

export interface WizardStep3Data {
  accepted: boolean;
  hasViewedTC: boolean;
}

interface BrokerRegistrationWizardStep3Props {
  broker: BrokerMetadata;
  step1Data: WizardStep1Data;
  data: WizardStep3Data;
  onChange: (data: WizardStep3Data) => void;
  error: string | null;
}

export function BrokerRegistrationWizardStep3({
  broker,
  step1Data,
  data,
  onChange,
  error,
}: BrokerRegistrationWizardStep3Props) {
  const displayName = broker.display_name || broker.broker_type;
  const [tcOpen, setTcOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid rgba(4, 55, 123, 0.1)',
          overflow: 'hidden',
          mb: 1,
        }}
      >
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'flex',
            flexDirection: 'column',
            '& > div': {
              display: 'flex',
              alignItems: 'stretch',
              '&:not(:last-child)': {
                borderBottom: '1px solid rgba(4, 55, 123, 0.1)',
              },
            },
            '& dt': {
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: 'text.secondary',
              m: 0,
              py: 1.5,
              px: 2,
              width: '140px',
              textAlign: 'right',
              borderRight: '1px solid rgba(4, 55, 123, 0.1)',
              bgcolor: 'rgba(4, 55, 123, 0.02)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            },
            '& dd': {
              m: 0,
              py: 1.5,
              px: 2,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              bgcolor: 'background.paper',
            },
          }}
        >
          <Box>
            <dt>Account Name</dt>
            <dd>{step1Data.accountName || '—'}</dd>
          </Box>
          <Box>
            <dt>{step1Data.authMode === 'oauth' ? 'Account ID' : 'User / Client ID'}</dt>
            <dd>{step1Data.accountId || '—'}</dd>
          </Box>
        </Box>
      </Box>

      <Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8125rem', lineHeight: 1.6 }}>
          To complete the connection, please review and accept the Terms & Conditions regarding broker integration and automated trading.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={data.accepted}
              disabled={!data.hasViewedTC}
              onChange={(e) => onChange({ ...data, accepted: e.target.checked })}
              sx={{
                color: 'rgba(4, 55, 123, 0.4)',
                '&.Mui-checked': { color: '#04377b' },
                '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.1)' }
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: data.hasViewedTC ? 'text.primary' : 'text.disabled', userSelect: 'none' }}>
              I have read and agree to the <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setTcOpen(true);
                  onChange({ ...data, hasViewedTC: true });
                }}
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#04377b',
                  textDecoration: 'underline',
                  verticalAlign: 'baseline',
                  p: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  cursor: 'pointer'
                }}
              >
                Terms & Conditions
              </Link>
            </Typography>
          }
          sx={{ mt: 0 }}
        />
      </Box>

      <Dialog
        open={tcOpen}
        onClose={() => setTcOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#04377b', pb: 1 }}>
          Terms & Conditions
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
            FlowRithm Broker Integration Policy
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.875rem' }}>
            By connecting your {displayName} account, you acknowledge and agree that:
            <ul style={{ paddingLeft: '20px', marginTop: '12px' }}>
              <li>You are responsible for all trades executed through this integration.</li>
              <li>API credentials and secrets are encrypted and stored securely on your local deployment.</li>
              <li>FlowRithm is not responsible for any financial losses incurred due to algorithmic errors or system failures.</li>
              <li>The connection requires active API access from {displayName} and may be subject to their specific terms and fees.</li>
              <li>Automated trading involves risks; ensure you have tested your strategies in a paper trading environment first.</li>
            </ul>
            Please ensure you have read and understood the broker's own API agreement before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTcOpen(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              bgcolor: '#04377b'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 3, fontSize: '0.8125rem' }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
